import React, { useCallback, useMemo, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useMatches, useNavigate, useParams } from 'react-router-dom';
import AnalogGatewaysTab from '../facility-analog-gateway/AnalogGatewaysTab';
import FacilityNumbersTab from '../facility-number/FacilityNumbersTab';
import { UserContext, useUser } from '../contexts/UserContext';
import { useFacilityById } from '../hooks/Facility';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { TabLoadingReporterProvider } from '../hooks/useTabLoadingReporter';
import FacilityDeviceGroupsTab from './FacilityDeviceGroupsTab';
import FacilityDetailsTab from './FacilityDetailsTab';
import FacilityGamesTab, { facilityGamesTabMeta } from './FacilityGamesTab';
import FacilityHousingUnitsTab from './FacilityHousingUnitsTab';
import FacilityNotesTab from './FacilityNotesTab';
import FacilityPageHeader from './FacilityPageHeader';
import FacilitySchedulesTab from './FacilitySchedulesTab';
import FacilityStationsTab from './FacilityStationsTab';
import FacilitySubscriptionsTab, { facilitySubscriptionsTabMeta } from './FacilitySubscriptionsTab';
import FacilityTrustAccountTab, { facilityTrustAccountTabMeta } from './FacilityTrustAccountTab';
import FacilityVisitsTab from './FacilityVisitsTab';
import FacilityAlertsTab from './FacilityAlertsTab';
import FacilityBlocksTab from './FacilityBlocksTab';
import FacilityConfigTab from './FacilityConfigTab';
import FacilityConfigurationTextsTab from './FacilityConfigurationTextsTab';
import FacilityDevicesTab from './FacilityDevicesTab';
import FacilityEquipmentTab from './FacilityEquipmentTab';
import FacilityInmatesTab from './FacilityInmatesTab';
import FacilityRoutingTab from './FacilityRoutingTab';
import FacilitySaltMinionsTab from './FacilitySaltMinionsTab';
import FacilitySpecialConditionsTab from './FacilitySpecialConditionsTab';
import FacilitySpeedDialTab from './FacilitySpeedDialTab';
import FacilityTextMessageAlertsTab from './FacilityTextMessageAlertsTab';
import FacilityVisitBoothsTab from './FacilityVisitBoothsTab';
import FacilityTvAudioTab from './FacilityTvAudioTab';

/** Route `eventKey` / `handle.tabId` values for facility detail tabs. */
const FACILITY_TAB_IDS = [
  'details',
  'notes',
  'devices',
  'inmates',
  'housing-units',
  'stations',
  'config',
  'alerts',
  'special-conditions',
  'blocks',
  'equipment',
  'visits',
  'visitation-booths',
  'schedules',
  'salt-minions',
  'analog-gateways',
  'tv-audio',
  'numbers',
  'routing',
  'configuration-texts',
  'device-groups',
  'text-message/alerts',
  'speed-dial/destinations',
  'subscriptions',
  'games',
  'trust-account',
] as const;

type FacilityTabId = (typeof FACILITY_TAB_IDS)[number];

type FacilityTab = {
  id: FacilityTabId;
  heading: string;
  style?: string;
  permissions?: string[];
};

const FACILITY_TABS: FacilityTab[] = [
  {
    id: 'details',
    heading: 'Details'
  },
  {
    id: 'notes',
    heading: 'Notes',
    style: 'fas fa-fw fa-lg fa-pencil-alt',
    permissions: ['facility_note_manage'],
  },
  {
    id: 'devices',
    heading: 'Devices',
    style: 'fas fa-fw fa-lg fa-mobile',
    permissions: ['mobile_device_manage'],
  },
  {
    id: 'inmates',
    heading: 'Inmates',
    style: 'fas fa-fw fa-lg fa-users',
    permissions: ['inmate_manage', 'inmate_search'],
  },
  {
    id: 'housing-units',
    heading: 'Housing Units',
    style: 'fas fa-fw fa-lg fa-cubes',
    permissions: ['housing_unit_manage'],
  },
  {
    id: 'stations',
    heading: 'Stations',
    style: 'fas fa-fw fa-lg fas fa-tablet',
    permissions: ['station_manage'],
  },
  {
    id: 'config',
    heading: 'Config',
    style: 'fas fa-fw fa-lg fas fa-cogs'
  },
  {
    id: 'alerts',
    heading: 'Alerts',
    style: 'fas fa-fw fa-lg fas fa-bell'
  },
  {
    id: 'special-conditions',
    heading: 'Special Conditions',
    style: 'fas fa-fw fa-lg fa-gift',
    permissions: ['condition_manage'],
  },
  {
    id: 'blocks',
    heading: 'Blocks',
    style: 'fas fa-fw fa-lg fas fa-lock',
    permissions: ['block_manage'],
  },
  {
    id: 'equipment',
    heading: 'Equipment',
    style: 'fas fa-fw fa-lg fas fa-cubes',
    permissions: ['facility_equipment_manage'],
  },
  {
    id: 'visits',
    heading: 'Visits',
    style: 'fas fa-fw fa-lg fa-users'
  },
  {
    id: 'visitation-booths',
    heading: 'Visit Booths',
    style: 'fas fa-fw fa-lg fa-person-booth',
    permissions: ['visitation_manage'],
  },
  {
    id: 'schedules',
    heading: 'Schedules',
    style: 'fas fa-fw fa-lg fa-calendar',
    permissions: ['schedule_manage'],
  },
  {
    id: 'salt-minions',
    heading: 'Salt Minions',
    style: 'fas fa-fw fa-lg fa-server',
    permissions: ['salt_manage'],
  },
  {
    id: 'analog-gateways',
    heading: 'Analog Gateways',
    style: 'fas fa-fw fa-lg fa-microchip',
    permissions: ['facility_analog_gateway_manage'],
  },
  {
    id: 'numbers',
    heading: 'Numbers',
    style: 'fas fa-fw fa-lg fa-tty',
    permissions: ['facility_number_manage'],
  },
  {
    id: 'routing',
    heading: 'Routing',
    style: 'fas fa-fw fa-lg fa-code-branch',
    permissions: ['call_route_manage'],
  },
  {
    id: 'configuration-texts',
    heading: 'Text Configurations',
    style: 'fas fa-fw fa-lg fa-file-alt',
  },
  {
    id: 'device-groups',
    heading: 'Device Groups',
    style: 'fas fa-fw fa-lg fa-object-group',
  },
  {
    id: 'text-message/alerts',
    heading: 'Text Message Alerts',
    style: 'fas fa-fw fa-lg fa-bell',
  },
  {
    id: 'speed-dial/destinations',
    heading: 'Speed Dial',
    style: 'fas fa-fw fa-lg fa-bolt',
  },
  {
    id: 'subscriptions',
    heading: facilitySubscriptionsTabMeta.heading,
    style: facilitySubscriptionsTabMeta.style,
    permissions: facilitySubscriptionsTabMeta.permissions,
  },
  {
    id: 'tv-audio',
    heading: 'TV Audio',
    style: 'fas fa-fw fa-lg fa-tv',
    permissions: ['tv_source_manage'],
  },
  {
    id: 'games',
    heading: facilityGamesTabMeta.heading,
    style: facilityGamesTabMeta.style,
    permissions: facilityGamesTabMeta.permissions,
  },
  {
    id: 'trust-account',
    heading: facilityTrustAccountTabMeta.heading,
    style: facilityTrustAccountTabMeta.style,
    permissions: facilityTrustAccountTabMeta.permissions,
  },
];

function facilityViewPath(facilityId: number, tabId: FacilityTabId): string {
  const base = `/facilities/view/${facilityId}`;
  if (tabId === 'details') {
    return base;
  }
  return `${base}/${tabId}`;
}

const FacilityDetailView: React.FC = () => {
  const { id: facilityIdParam } = useParams();
  const facilityId = useMemo(() => {
    const n = Number(facilityIdParam);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [facilityIdParam]);
  const { execute: handleRefresh, result, status } = useFacilityById(facilityId);
  const facility = result ?? null;
  const navigate = useNavigate();
  const matches = useMatches();
  const user = useUser();

  useDocumentTitle(facility?.name);

  const [tabLoading, setTabLoading] = useState<Partial<Record<FacilityTabId, boolean>>>({});

  const reportTabLoading = useCallback((tabId: string, loading: boolean) => {
    const id = tabId as FacilityTabId;
    setTabLoading((prev) => (prev[id] === loading ? prev : { ...prev, [id]: loading }));
  }, []);

  const visibleTabs = FACILITY_TABS.filter((t) => !t.permissions || user.hasOneOfPermissions(t.permissions));

  const visibleTabIds = useMemo(() => visibleTabs.map((t) => t.id) as FacilityTabId[], [visibleTabs]);

  // Determine the current tab from the route instead of storing it the local state. This prevents
  // double API requests when the tab is selected.
  const activeTab = useMemo((): FacilityTabId => {
    const lastWithTabId = [...matches].reverse().find((m) => {
      const h = m.handle as { tabId?: string } | undefined;
      return typeof h?.tabId === 'string';
    });
    const raw = (lastWithTabId?.handle as { tabId?: string } | undefined)?.tabId ?? 'details';
    const routeTabId = raw as FacilityTabId;
    return visibleTabIds.includes(routeTabId) ? routeTabId : 'details';
  }, [matches, visibleTabIds]);

  const handleSelectTab = useCallback(
    (key: string | null) => {
      if (key == null || facility?.id == null) {
        return;
      }
      navigate(facilityViewPath(facility.id, key as FacilityTabId));
    },
    [facility?.id, navigate]
  );

  if (facilityId == null) {
    return (
      <div className="alert alert-danger" role="alert">
        Invalid facility id.
      </div>
    );
  }

  if (status === 'loading' || status === 'not-requested') {
    return (
      <div className="row">
        <div className="col-sm-12 col-sm-7 col-md-7 col-lg-4">
          <h1 className="page-title txt-color-blueDark">
            <i className="fas fa-sm fa-fw fa-building" /> Loading…
          </h1>
        </div>
      </div>
    );
  }

  if (facility == null) {
    return (
      <div className="alert alert-danger" role="alert">
        Facility not found.
      </div>
    );
  }

  const renderTabBody = (tabId: FacilityTabId) => {
    switch (tabId) {
      case 'details':
        return <FacilityDetailsTab facility={facility} />;
      case 'notes':
        return <FacilityNotesTab facility={facility} />;
      case 'housing-units':
        return <FacilityHousingUnitsTab facility={facility} />;
      case 'stations':
        return <FacilityStationsTab facility={facility} />;
      case 'visits':
        return <FacilityVisitsTab facility={facility} />;
      case 'schedules':
        return <FacilitySchedulesTab facility={facility} />;
      case 'device-groups':
        return <FacilityDeviceGroupsTab facility={facility} />;
      case 'subscriptions':
        return <FacilitySubscriptionsTab facility={facility} />;
      case 'games':
        return <FacilityGamesTab facility={facility} />;
      case 'trust-account':
        return <FacilityTrustAccountTab facility={facility} />;
      case 'numbers':
        return <FacilityNumbersTab facility={facility} />;
      case 'devices':
        return <FacilityDevicesTab facility={facility} />;
      case 'inmates':
        return <FacilityInmatesTab facility={facility} />;
      case 'config':
        return <FacilityConfigTab facility={facility} />;
      case 'alerts':
        return <FacilityAlertsTab facility={facility} />;
      case 'special-conditions':
        return <FacilitySpecialConditionsTab facility={facility} />;
      case 'blocks':
        return <FacilityBlocksTab facility={facility} />;
      case 'equipment':
        return <FacilityEquipmentTab facility={facility} />;
      case 'visitation-booths':
        return <FacilityVisitBoothsTab facility={facility} />;
      case 'salt-minions':
        return <FacilitySaltMinionsTab facility={facility} />;
      case 'analog-gateways':
        return <AnalogGatewaysTab facility={facility} />;
      case 'tv-audio':
        return <FacilityTvAudioTab facility={facility} />;
      case 'routing':
        return <FacilityRoutingTab facility={facility} />;
      case 'configuration-texts':
        return <FacilityConfigurationTextsTab facility={facility} />;
      case 'text-message/alerts':
        return <FacilityTextMessageAlertsTab facility={facility} />;
      case 'speed-dial/destinations':
        return <FacilitySpeedDialTab facility={facility} />;
      default:
        return null;
    }
  };

  return (
    <UserContext.Provider value={user}>
      <FacilityPageHeader facility={facility} onRefresh={handleRefresh} />
      <TabLoadingReporterProvider value={reportTabLoading}>
        <Tabs activeKey={activeTab} onSelect={handleSelectTab} id="facility-detail-tabs" className="mb-3">
          {visibleTabs.map((t) => (
            <Tab
              key={t.id}
              eventKey={t.id}
              title={
                <span>
                  {t.style && <i className={`${t.style} me-1`} />}
                  {t.heading}
                  {tabLoading[t.id] && (
                    <span>
                      {' '}
                      <i className="fas fa-spin fa-spinner" />
                    </span>
                  )}
                </span>
              }>
              {activeTab === t.id && renderTabBody(t.id)}
            </Tab>
          ))}
        </Tabs>
      </TabLoadingReporterProvider>
    </UserContext.Provider>
  );
};

export default FacilityDetailView;
